import {
    AddEditInventoryProps,
    InventoryTableInnerProps,
} from '../../models/table-models';
import { InventoryContent, PassportItemType } from '../../models/warehouse-models';
import { useTranslation } from 'react-i18next';
import { CustomTable } from '../../components/CustomTable';
import {inventoryCollapsedTableHeaders, inventoryTableHeaders} from '../../utils/Enums/TableHeaders';
import { PaginationComponent } from '../../components/Pagination';
import { NoDataComponent } from '../../components/NoDataComponent';
import React from 'react';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { formatQuantitiesByPassport } from '../../utils/helper-functions';
import {
    checkForItemsInInventory,
    returnItem,
    useGetAllScraps,
    useGetAllShortOtherMaterialPassports,
    useGetReferences,
    useGetReferencesForDistributor, useGetReturnedItems,
    useGetUserInventory,
} from '../../utils/warehouses-hooks';
import { useForm } from 'react-hook-form';
import { resourceCache } from 'use-async-resource';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../../components/CustomToastContent';
import { Form } from 'react-bootstrap';
import CommonInputText from '../../components/CommonInputText';

export const calcOilPercentage = (current: number, total: number) => {
    return `${((current * 100) / total).toFixed()}% (${current} ml)`;
};

export const oilAmount = (availableAmount?: number) => {
    return availableAmount ?? '';
};

const textEnd = (value: React.ReactNode) => <div className='text-end'>{value}</div>;
const truncateText = (value?: string, maxLength: number = 40) => {
    if (!value || value.length <= maxLength) return value ?? '';
    return `${value.slice(0, maxLength)}...`;
};

export const inventoryTypeOrder: Array<PassportItemType | 'UNKNOWN'> = ['DEVICE', 'OIL', 'BATTERY', 'CONTAINER', 'OTHER', 'UNKNOWN'];
type InventoryTypeRow = {
    cells: React.ReactNode[];
    className?: string;
    insertIndex?: number;
};

export const getInventoryType = (item: InventoryContent): PassportItemType | 'UNKNOWN' => {
    return item.type ?? item.item ?? 'UNKNOWN';
};

export const InventoryInner = ({ getEntities, pagination, refetchEntitiesHook, userId, showSelect= false, showPagination=true, showRows, allowEdit = true }: InventoryTableInnerProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { items, pager } = getEntities();

    const formatQuantitiesByPassportID = (items: InventoryContent[]) => {
        return formatQuantitiesByPassport(
            items,
            (item: InventoryContent) => item.idOfPassport,
            (existingItem: InventoryContent, newItem: InventoryContent) => {
                if (existingItem.batchCode!= null && !existingItem.batchCode.includes(newItem.batchCode)) {
                    existingItem.batchCode = existingItem.batchCode.concat(", ").concat(newItem.batchCode);
                }
                existingItem.count += newItem.count;
                existingItem.cost += newItem.cost;
                existingItem.availableAmount = (existingItem.availableAmount ?? 0) + (newItem.availableAmount ?? 0);
                existingItem.collectionOfItems?.push(newItem);
            },
            (item: InventoryContent) => {
                item.cost /= 100;
            }
        );
    };
    const formattedInventoryItems = formatQuantitiesByPassportID(items).sort((firstItem, secondItem) => {
        const firstTypeIndex = inventoryTypeOrder.indexOf(getInventoryType(firstItem));
        const secondTypeIndex = inventoryTypeOrder.indexOf(getInventoryType(secondItem));

        if (firstTypeIndex !== secondTypeIndex) return firstTypeIndex - secondTypeIndex;
        return firstItem.passport.localeCompare(secondItem.passport);
    });
    const inventoryTypeRows = formattedInventoryItems.reduce<InventoryTypeRow[]>(
        (rows, item, index) => {
            const type = getInventoryType(item);
            const previousItem = formattedInventoryItems[index - 1];

            if (!previousItem || getInventoryType(previousItem) !== type) {
                rows.push({
                    insertIndex: index,
                    className: 'fw-bold background-dark color-main',
                    cells: [type === 'UNKNOWN' ? t('withoutType') : t(type), '', '', ''],
                });
            }

            return rows;
        },
        []
    );
    const handleReturnItem = async (item: InventoryContent) => {
        await returnItem({
            passportId: item.idOfPassport,
            contentId: item.id ?? 0,
            count: item.count,
            passportName: item.passport,
            userName: '',
            userId: userId ?? 0,
            batchCode: item.batchCode,
            cost: item.cost,
            state: item.state
        });
    };


    return (
        <>
            {items && items.length > 0 ? (
                <>
                    <CustomTable
                        selectable={showSelect}
                        columns={{
                            groupByField: (item) => item?.passport,
                            headings: inventoryTableHeaders,
                            collectionOfItems: items,
                            collapsedHeadings: inventoryCollapsedTableHeaders,
                            innerTableFields: [{field: 'passport'}, {field: 'count'}, {field: 'batchCode'}, {field: 'state',isTranslate:true}],
                            items: formattedInventoryItems.map(
                                ({ passport, count, batchCode, availableAmount }) => [
                                    passport,
                                    truncateText(batchCode),
                                    textEnd(count),
                                    textEnd(oilAmount(availableAmount)),
                                ]
                            ),
                        }}
                        tableData={formattedInventoryItems}
                        editInnerComponent={allowEdit ? ({itemToEdit, setShowModal, refetchEntitiesHook}) => <AddEditInventory {...{itemToEdit, setShowModal, refetchEntitiesHook, userId}}/> : undefined}
                        editIcon={allowEdit ? faTrash : undefined}
                        // actionHook={handleReturnItem}
                        hasAccordionRow={true}
                        customRows={inventoryTypeRows}
                        {...{ refetchEntitiesHook }}
                        tableFooterText={(item) => {
                            return `${t('quantity')}: ${item.count}`;
                        }}
                    />
                    {showPagination &&  <PaginationComponent
                        recordsCount={pager.totalCount}
                        pagination={{ ...pagination, pageSize: pager.itemsPerPage }}
                    />}
                    {showRows && <p><strong>{t('numberOfRows')}</strong> {items.length}</p>}
                   
                </>
            ) : (
                <NoDataComponent noDataText={t('resources')}></NoDataComponent>
            )}
        </>
    );
};

const AddEditInventory = ({ itemToEdit, setShowModal, refetchEntitiesHook, userId }: AddEditInventoryProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const { register, handleSubmit, formState, control, resetField } = useForm<InventoryContent>({
        mode: 'all',
    });

    const useSubmitHandlerEdit = async (formValue: InventoryContent) => {
        await returnItem({
            passportId: itemToEdit?.idOfPassport ?? 0,
            contentId: itemToEdit?.id ?? 0,
            count: formValue.count,
            passportName: itemToEdit?.passport ?? '',
            userName: '',
            userId: userId,
            batchCode: itemToEdit?.batchCode ?? '',
            cost: itemToEdit?.cost ?? 0,
            state: itemToEdit?.state ?? ''
        });
        toast.success(<CustomToastContent content={[t('itemReturned')]} whiteText />, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
        });
        refetchEntitiesHook();
        resourceCache(useGetUserInventory).clear();
        resourceCache(checkForItemsInInventory).clear();
        resourceCache(useGetAllShortOtherMaterialPassports).clear();
        resourceCache(useGetAllScraps).clear();
        resourceCache(useGetReferencesForDistributor).clear();
        resourceCache(useGetReferences).clear();
        resourceCache(useGetReturnedItems).clear();
        setShowModal(false);
    };

    return (
        <Form
            id='form'
            className='d-flex flex-column align-items-center p-4  w-100'
            noValidate
            onSubmit={handleSubmit(useSubmitHandlerEdit)}
        >
            <Form.Group className=' d-flex flex-column mb-0 align-items-start w-100 ' controlId='count'>
                <CommonInputText
                    id='count'
                    label={t('wantedAmount')}
                    labelTextColor='text-black'
                    type='number'
                    fieldError={formState.errors.count}
                    defaultValue={'0'}
                    hookRegister={register('count', {
                        max: itemToEdit?.count,
                        min: 1,
                        required: {
                            value: true,
                            message: t('count'),
                        },
                    })}
                />
            </Form.Group>
        </Form>
    );
};
